import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Home as HomeIcon, Heart as HeartIcon, ShoppingBag as ShoppingBagIcon, Truck as TruckIcon, ArrowLeft, Loader, Calendar, User } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { API_URL, resolveImageUrl } from '../config';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './Blog.css';



const defaultPosts = [
  {
    id: 1,
    title: "Healthy Living with Himalayan Nuts",
    excerpt: "Discover the health benefits of incorporating organic Himalayan nuts and seeds into your daily diet.",
    content: "Himalayan nuts are packed with healthy monounsaturated fats, fiber, vitamins, and minerals. Sourced directly from natural orchards, they are free from chemical additives. Regular consumption helps lower bad cholesterol, support digestive wellness, and provides sustained natural energy. Try adding a handful of almonds and walnuts to your morning routine to jumpstart your health journey.",
    image: "https://images.unsplash.com/photo-1543362906-acfc16c67564?auto=format&fit=crop&q=80&w=600",
    date: "July 10, 2026",
    author: "Dr. Aarav Mehta"
  },
  {
    id: 2,
    title: "The Superfood Qualities of Makhana",
    excerpt: "Makhana (Fox Nuts) is the perfect guilt-free snack. Learn why this superfood is gaining global popularity.",
    content: "Fox nuts, commonly known as Makhana, are gluten-free, low in calories, and rich in calcium, iron, and antioxidants. Sourced from the clean ponds of the Himalayas, our makhanas are processed naturally. They are perfect for snacking between meals, supporting weight management, and strengthening bone health. Lightly roast them with organic ghee and pepper for a delicious, healthy snack.",
    image: "https://images.unsplash.com/photo-1596547609652-9cf5d8d76921?auto=format&fit=crop&q=80&w=600",
    date: "July 08, 2026",
    author: "Priya Sharma"
  }
];

const Blog = () => {
  const navigate = useNavigate();
  const { wishlistCount, cartCount } = useCart();
  const [posts, setPosts] = useState(defaultPosts);
  const [loading, setLoading] = useState(true);
  const [activePost, setActivePost] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activePost]);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/settings/blog_posts`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load blog posts');
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data)) {
          setPosts(data);
        }
      })
      .catch((err) => console.error('Error fetching blog posts:', err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="blog-page-wrapper">
      {/* Top Announcement Bar */}
      <TopBar />

      {/* Main Header */}
      <Header 
        wishlistCount={wishlistCount} 
        cartCount={cartCount} 
        onCartClick={() => navigate('/cart')} 
      />

      {/* Navigation Bar */}
      <Navbar activeItem="Blog" />

      {/* Main Content */}
      <main className="blog-main">
        <div className="container">
          <div className="blog-header-row">
            {activePost ? (
              <button onClick={() => setActivePost(null)} className="back-btn" style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ArrowLeft size={16} />
                <span>Back to Blog Articles</span>
              </button>
            ) : (
              <Link to="/" className="back-btn">
                <ArrowLeft size={16} />
                <span>Back to Shop</span>
              </Link>
            )}
            <h1 className="blog-page-title">
              {activePost ? "Read Article" : "Healthy Living Blog"}
            </h1>
          </div>

          {loading ? (
            <div className="blog-loader-container">
              <Loader className="spinner" size={40} />
              <p>Loading healthy insights...</p>
            </div>
          ) : activePost ? (
            /* Full Article View */
            <article className="full-article-content">
              {activePost.image && (
                <div className="article-banner-wrapper">
                  <img 
                    src={resolveImageUrl(activePost.image)} 
                    alt={activePost.title} 
                    className="article-banner-img"
                  />
                </div>
              )}
              
              <div className="article-meta-row">
                <span className="meta-item">
                  <Calendar size={14} />
                  <span>{activePost.date}</span>
                </span>
                <span className="meta-item">
                  <User size={14} />
                  <span>By {activePost.author}</span>
                </span>
              </div>

              <h2 className="article-title-heading">{activePost.title}</h2>
              
              <div className="article-body-text">
                {activePost.content.split('\n').map((para, idx) => (
                  <p key={idx} className="article-p">{para}</p>
                ))}
              </div>

              <div style={{ marginTop: '40px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                <button onClick={() => setActivePost(null)} className="btn-back-to-list">
                  Back to Articles
                </button>
              </div>
            </article>
          ) : (
            /* Articles List Grid */
            <div className="blog-posts-grid">
              {posts.map((post) => (
                <div key={post.id} className="blog-post-card">
                  {post.image && (
                    <div className="post-card-img-box">
                      <img 
                        src={resolveImageUrl(post.image)} 
                        alt={post.title} 
                        className="post-card-img"
                      />
                    </div>
                  )}
                  <div className="post-card-body">
                    <div className="post-card-meta">
                      <span className="meta-date">{post.date}</span>
                      <span className="meta-dot">•</span>
                      <span className="meta-author">{post.author}</span>
                    </div>
                    <h3 className="post-card-title">{post.title}</h3>
                    <p className="post-card-excerpt">{post.excerpt}</p>
                    <button 
                      onClick={() => setActivePost(post)} 
                      className="btn-read-more"
                    >
                      Read Article
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <Footer />

      {/* Sticky Bottom Navigation (Mobile screens only) */}
      <div className="mobile-sticky-nav" role="navigation" aria-label="Mobile Navigation">
        <Link 
          to="/" 
          className="mobile-nav-item"
        >
          <HomeIcon size={20} />
          <span>Home</span>
        </Link>
        <Link 
          to="/track" 
          className="mobile-nav-item"
        >
          <TruckIcon size={20} />
          <span>Track Order</span>
        </Link>
        <Link 
          to="/wishlist" 
          className="mobile-nav-item"
        >
          <HeartIcon size={20} />
          {wishlistCount > 0 && (
            <span className="badge-count">{wishlistCount}</span>
          )}
          <span>Wishlist</span>
        </Link>
        <Link 
          to="/cart" 
          className="mobile-nav-item"
        >
          <ShoppingBagIcon size={20} />
          {cartCount > 0 && (
            <span className="badge-count">{cartCount}</span>
          )}
          <span>Cart</span>
        </Link>
      </div>
    </div>
  );
};

export default Blog;
